import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { leadsAPI } from "../../lib/api";
import { Users, Search, Plus, Filter, Trash2, Eye, MessageCircle, Mail, Tag } from "lucide-react";
import { toast } from "sonner";
import { Header } from '../../components/layout/Header';

const SEGMENTO_OPTIONS = [
    { value: "", label: "Todos os segmentos" },
    { value: "mulheres_com_proposito", label: "Mulheres com Propósito" },
    { value: "mesa_de_ester",          label: "Mesa de Ester" },
    { value: "luto_ao_recomeco",       label: "Do Luto ao Recomeço" },
    { value: "devocional",             label: "Devocional" },
    { value: "musica",                 label: "Música" },
    { value: "livro",                  label: "Livro" },
    { value: "geral",                  label: "Geral" },
];

const STATUS_OPTIONS = [
    { value: "", label: "Todos os status" },
    { value: "novo",       label: "Novo" },
    { value: "engajado",   label: "Engajado" },
    { value: "convertido", label: "Convertido" },
    { value: "inativo",    label: "Inativo" },
];

const SEGMENTO_COLORS = {
    mulheres_com_proposito: "bg-amber-800/40 text-amber-300 border-amber-700",
    mesa_de_ester:          "bg-rose-800/40 text-rose-300 border-rose-700",
    luto_ao_recomeco:       "bg-indigo-800/40 text-indigo-300 border-indigo-700",
    devocional:             "bg-sky-800/40 text-sky-300 border-sky-700",
    musica:                 "bg-violet-800/40 text-violet-300 border-violet-700",
    livro:                  "bg-emerald-800/40 text-emerald-300 border-emerald-700",
    geral:                  "bg-stone-700/40 text-stone-300 border-stone-600",
};

const STATUS_COLORS = {
    novo:       "bg-blue-900/40 text-blue-300 border-blue-700",
    engajado:   "bg-amber-900/40 text-amber-300 border-amber-700",
    convertido: "bg-green-900/40 text-green-300 border-green-700",
    inativo:    "bg-stone-700/40 text-stone-400 border-stone-600",
};

function timeAgo(iso) {
    if (!iso) return "—";
    const diff = Date.now() - new Date(iso).getTime();
    const m = Math.floor(diff / 60000);
    if (m < 1) return "agora";
    if (m < 60) return `${m}m`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h`;
    return `${Math.floor(h / 24)}d`;
}

function Avatar({ name = "" }) {
    const initials = name.split(" ").slice(0,2).map(w => w[0]).join("").toUpperCase() || "?";
    const colors = ["bg-amber-700","bg-rose-700","bg-indigo-700","bg-sky-700","bg-violet-700","bg-emerald-700"];
    const bg = colors[(name.charCodeAt(0) || 0) % colors.length];
    return (
        <div className={`w-9 h-9 rounded-full ${bg} flex items-center justify-center flex-shrink-0`}>
            <span className="text-xs font-bold text-white">{initials}</span>
        </div>
    );
}

// Modal de novo lead
function NewLeadModal({ open, onClose, onCreated }) {
    const [form, setForm] = useState({ name: "", email: "", phone: "", segmento: "geral", origem: "manual", opted_in_email: true, opted_in_whatsapp: false, tags: [], notes: "" });
    const [saving, setSaving] = useState(false);

    const save = async () => {
        if (!form.name) { toast.error("Nome obrigatório"); return; }
        setSaving(true);
        try {
            await leadsAPI.create(form);
            toast.success("Lead criado");
            onCreated();
            onClose();
            setForm({ name: "", email: "", phone: "", segmento: "geral", origem: "manual", opted_in_email: true, opted_in_whatsapp: false, tags: [], notes: "" });
        } catch (e) {
            toast.error(e.response?.data?.detail || "Erro ao criar lead");
        } finally { setSaving(false); }
    };

    if (!open) return null;
    return (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div className="bg-stone-900 border border-stone-700 rounded-2xl w-full max-w-md shadow-2xl">
                <div className="p-5 border-b border-stone-800">
                    <h2 className="text-base font-bold text-stone-100">Novo Lead</h2>
                </div>
                <div className="p-5 space-y-3">
                    <div>
                        <label className="text-xs text-stone-400 block mb-1">Nome *</label>
                        <input className="w-full bg-stone-800 border border-stone-700 rounded-lg px-3 py-2 text-sm text-stone-100 placeholder:text-stone-600 focus:outline-none focus:border-amber-600"
                            value={form.name} onChange={e => setForm(f => ({...f, name: e.target.value}))} placeholder="Nome completo" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="text-xs text-stone-400 block mb-1">Email</label>
                            <input type="email" className="w-full bg-stone-800 border border-stone-700 rounded-lg px-3 py-2 text-sm text-stone-100 placeholder:text-stone-600 focus:outline-none focus:border-amber-600"
                                value={form.email} onChange={e => setForm(f => ({...f, email: e.target.value}))} placeholder="email@..." />
                        </div>
                        <div>
                            <label className="text-xs text-stone-400 block mb-1">Telefone</label>
                            <input className="w-full bg-stone-800 border border-stone-700 rounded-lg px-3 py-2 text-sm text-stone-100 placeholder:text-stone-600 focus:outline-none focus:border-amber-600"
                                value={form.phone} onChange={e => setForm(f => ({...f, phone: e.target.value}))} placeholder="47 9..." />
                        </div>
                    </div>
                    <div>
                        <label className="text-xs text-stone-400 block mb-1">Jornada / Segmento</label>
                        <select className="w-full bg-stone-800 border border-stone-700 rounded-lg px-3 py-2 text-sm text-stone-100 focus:outline-none focus:border-amber-600"
                            value={form.segmento} onChange={e => setForm(f => ({...f, segmento: e.target.value}))}>
                            {SEGMENTO_OPTIONS.filter(s => s.value).map(s => (
                                <option key={s.value} value={s.value}>{s.label}</option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="text-xs text-stone-400 block mb-1">Origem</label>
                        <input className="w-full bg-stone-800 border border-stone-700 rounded-lg px-3 py-2 text-sm text-stone-100 placeholder:text-stone-600 focus:outline-none focus:border-amber-600"
                            value={form.origem} onChange={e => setForm(f => ({...f, origem: e.target.value}))} placeholder="manual, instagram, evento..." />
                    </div>
                    <div className="flex items-center gap-4">
                        <label className="flex items-center gap-2 text-xs text-stone-300 cursor-pointer">
                            <input type="checkbox" className="rounded" checked={form.opted_in_email}
                                onChange={e => setForm(f => ({...f, opted_in_email: e.target.checked}))} />
                            Opt-in Email
                        </label>
                        <label className="flex items-center gap-2 text-xs text-stone-300 cursor-pointer">
                            <input type="checkbox" className="rounded" checked={form.opted_in_whatsapp}
                                onChange={e => setForm(f => ({...f, opted_in_whatsapp: e.target.checked}))} />
                            Opt-in WhatsApp
                        </label>
                    </div>
                    <div>
                        <label className="text-xs text-stone-400 block mb-1">Notas</label>
                        <textarea rows={2} className="w-full bg-stone-800 border border-stone-700 rounded-lg px-3 py-2 text-sm text-stone-100 placeholder:text-stone-600 focus:outline-none focus:border-amber-600 resize-none"
                            value={form.notes} onChange={e => setForm(f => ({...f, notes: e.target.value}))} placeholder="Observações..." />
                    </div>
                </div>
                <div className="p-5 border-t border-stone-800 flex justify-end gap-2">
                    <button onClick={onClose} className="px-4 py-2 text-sm text-stone-400 hover:text-stone-200 transition-colors">Cancelar</button>
                    <button onClick={save} disabled={saving}
                        className="px-4 py-2 bg-amber-700 hover:bg-amber-600 text-white text-sm rounded-lg transition-colors disabled:opacity-50">
                        {saving ? "Salvando..." : "Criar Lead"}
                    </button>
                </div>
            </div>
        </div>
    );
}

export default function LeadsAdmin() {
    const [leads, setLeads] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [segmento, setSegmento] = useState("");
    const [status, setStatus] = useState("");
    const [page, setPage] = useState(0);
    const [showNew, setShowNew] = useState(false);
    const LIMIT = 25;

    const load = async () => {
        setLoading(true);
        try {
            const params = { skip: page * LIMIT, limit: LIMIT };
            if (search)   params.search   = search;
            if (segmento) params.segmento = segmento;
            if (status)   params.status   = status;
            const res = await leadsAPI.list(params);
            setLeads(res.data.leads || []);
            setTotal(res.data.total || 0);
        } catch { toast.error("Erro ao carregar leads"); }
        finally { setLoading(false); }
    };

    useEffect(() => { load(); }, [page, segmento, status]);
    useEffect(() => {
        const t = setTimeout(load, 400);
        return () => clearTimeout(t);
    }, [search]);

    const deleteLead = async (id) => {
        if (!window.confirm("Excluir este lead?")) return;
        try { await leadsAPI.delete(id); toast.success("Lead excluído"); load(); }
        catch { toast.error("Erro ao excluir"); }
    };

    return (
        <>
        <Header title="Leads" subtitle={`${total.toLocaleString('pt-BR')} contatos`} actions={<button onClick={() => setShowNew(true)} className="flex items-center gap-2 bg-amber-700 hover:bg-amber-600 text-white text-sm px-4 py-2 rounded-lg transition-colors"><Plus className="w-4 h-4" /> Novo Lead</button>} />
        <div className="p-6 space-y-5">
            <NewLeadModal open={showNew} onClose={() => setShowNew(false)} onCreated={load} />


            {/* Filtros */}
            <div className="flex items-center gap-3 flex-wrap">
                <div className="flex items-center gap-2 bg-stone-900 border border-stone-800 rounded-lg px-3 py-2 flex-1 min-w-[200px]">
                    <Search className="w-4 h-4 text-stone-500 flex-shrink-0" />
                    <input className="bg-transparent text-sm text-stone-100 placeholder:text-stone-600 focus:outline-none flex-1"
                        placeholder="Buscar por nome, email ou telefone..."
                        value={search} onChange={e => setSearch(e.target.value)} />
                </div>
                <select value={segmento} onChange={e => { setSegmento(e.target.value); setPage(0); }}
                    className="bg-stone-900 border border-stone-800 rounded-lg px-3 py-2 text-sm text-stone-300 focus:outline-none focus:border-amber-600">
                    {SEGMENTO_OPTIONS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
                <select value={status} onChange={e => { setStatus(e.target.value); setPage(0); }}
                    className="bg-stone-900 border border-stone-800 rounded-lg px-3 py-2 text-sm text-stone-300 focus:outline-none focus:border-amber-600">
                    {STATUS_OPTIONS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
            </div>

            {/* Tabela */}
            <div className="bg-stone-900 border border-stone-800 rounded-xl overflow-hidden">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="border-b border-stone-800">
                            <th className="text-left px-4 py-3 text-xs text-stone-500 font-medium">Contato</th>
                            <th className="text-left px-4 py-3 text-xs text-stone-500 font-medium">Jornada</th>
                            <th className="text-left px-4 py-3 text-xs text-stone-500 font-medium">Status</th>
                            <th className="text-left px-4 py-3 text-xs text-stone-500 font-medium">Canais</th>
                            <th className="text-left px-4 py-3 text-xs text-stone-500 font-medium">Origem</th>
                            <th className="text-left px-4 py-3 text-xs text-stone-500 font-medium">Cadastro</th>
                            <th className="px-4 py-3" />
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-stone-800/50">
                        {loading ? (
                            <tr><td colSpan={7} className="text-center py-12 text-stone-500 text-xs">Carregando...</td></tr>
                        ) : leads.length === 0 ? (
                            <tr><td colSpan={7} className="text-center py-12 text-stone-500 text-xs">Nenhum lead encontrado</td></tr>
                        ) : leads.map(lead => (
                            <tr key={lead.id} className="hover:bg-stone-800/30 transition-colors group">
                                <td className="px-4 py-3">
                                    <div className="flex items-center gap-3">
                                        <Avatar name={lead.name} />
                                        <div className="min-w-0">
                                            <p className="text-stone-100 font-medium truncate">{lead.name}</p>
                                            <p className="text-xs text-stone-500 truncate">{lead.email || lead.phone || "—"}</p>
                                        </div>
                                    </div>
                                </td>
                                <td className="px-4 py-3">
                                    <span className={`text-xs px-2 py-0.5 rounded-full border ${SEGMENTO_COLORS[lead.segmento] || SEGMENTO_COLORS.geral}`}>
                                        {SEGMENTO_OPTIONS.find(s => s.value === lead.segmento)?.label || lead.segmento}
                                    </span>
                                </td>
                                <td className="px-4 py-3">
                                    <span className={`text-xs px-2 py-0.5 rounded-full border ${STATUS_COLORS[lead.status] || STATUS_COLORS.novo}`}>
                                        {lead.status}
                                    </span>
                                </td>
                                <td className="px-4 py-3">
                                    <div className="flex items-center gap-1.5">
                                        {lead.opted_in_whatsapp && <MessageCircle className="w-3.5 h-3.5 text-green-400" title="WhatsApp" />}
                                        {lead.opted_in_email && <Mail className="w-3.5 h-3.5 text-sky-400" title="Email" />}
                                        {lead.tags?.length > 0 && <Tag className="w-3.5 h-3.5 text-amber-400" title={lead.tags.join(", ")} />}
                                    </div>
                                </td>
                                <td className="px-4 py-3">
                                    <span className="text-xs text-stone-500">{lead.origem || "—"}</span>
                                </td>
                                <td className="px-4 py-3">
                                    <span className="text-xs text-stone-500">{timeAgo(lead.created_at)}</span>
                                </td>
                                <td className="px-4 py-3">
                                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                        <Link to={`/leads/${lead.id}`}
                                            className="p-1.5 rounded-lg text-stone-400 hover:text-stone-100 hover:bg-stone-700 transition-all">
                                            <Eye className="w-3.5 h-3.5" />
                                        </Link>
                                        <button onClick={() => deleteLead(lead.id)}
                                            className="p-1.5 rounded-lg text-stone-400 hover:text-red-400 hover:bg-red-900/20 transition-all">
                                            <Trash2 className="w-3.5 h-3.5" />
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>

                {/* Paginação */}
                {total > LIMIT && (
                    <div className="border-t border-stone-800 px-4 py-3 flex items-center justify-between">
                        <span className="text-xs text-stone-500">{page * LIMIT + 1}–{Math.min((page+1)*LIMIT, total)} de {total}</span>
                        <div className="flex gap-2">
                            <button disabled={page === 0} onClick={() => setPage(p => p-1)}
                                className="px-3 py-1 text-xs border border-stone-700 rounded-lg text-stone-400 hover:text-stone-200 disabled:opacity-30 transition-colors">
                                Anterior
                            </button>
                            <button disabled={(page+1)*LIMIT >= total} onClick={() => setPage(p => p+1)}
                                className="px-3 py-1 text-xs border border-stone-700 rounded-lg text-stone-400 hover:text-stone-200 disabled:opacity-30 transition-colors">
                                Próxima
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
        </>
    );
}
